import { usePage, router } from "@inertiajs/react";

import RootLayout from "@/components/root_layout";
import { Link } from "@/components/ui/link";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Plus } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";

export default function TeamLayout({ children }) {
  const handleLogout = () => router.delete("/sign_out");
  const { current_user, current_team, asset_types, permissions, user_teams } = usePage().props;
  const slug = current_team.slug;

  return (
    <RootLayout>
      <SidebarProvider>
        <Sidebar>
          <SidebarHeader className="h-16 justify-center border-b">
            <Link href={`/${slug}/dashboard`} className="px-2 font-semibold text-lg">bookd.one</Link>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link href={`/${slug}/dashboard`}>Dashboard</Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {asset_types.map(at => (
                    <SidebarMenuItem key={at.id}>
                      <SidebarMenuButton asChild>
                        <Link href={`/${slug}/asset_types/${at.id}/bookings/new`}>
                          Book a {at.name}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Team settings</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link href={`/${slug}/members`}>Manage Teammates</Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {permissions.assets.update && asset_types.map(at => (
                    <SidebarMenuItem key={at.id}>
                      <SidebarMenuButton asChild>
                        <Link href={`/${slug}/asset_types/${at.id}/assets/edit`}>
                          Manage {at.name_plural}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <p className="px-2 text-xs text-muted-foreground">Current team</p>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                      <div className="flex flex-col flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold">{current_team.name}</span>
                      </div>
                      <ChevronsUpDown className="ml-auto size-4" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top" className="w-[--radix-dropdown-menu-trigger-width] min-w-56">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Teams</DropdownMenuLabel>
                    {user_teams.map(team => (
                      <DropdownMenuItem key={team.id} asChild>
                        <Link href={`/${team.slug}/dashboard`}>{team.name}</Link>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/teams/new">
                        <Plus className="size-4" />
                        New team
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>

        <SidebarInset>
          <header className="sticky top-0 z-50 bg-white flex h-16 items-center px-6 justify-between border-b">
            <nav className="flex items-baseline gap-6 text-sm">
              <SidebarTrigger className="-ml-1" />
            </nav>
            <nav>
              <Button size="sm" onClick={handleLogout}>Log out</Button>
            </nav>
          </header>

          <main className="flex flex-1">
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </RootLayout>
  );
}
